import React, { useMemo, useState, useRef, useEffect } from "react";

// @material-ui/core components
import { makeStyles } from "tss-react/mui";

// lexical
import { $getRoot, $createParagraphNode, $createTextNode, $getSelection } from 'lexical';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { LinkNode } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $generateHtmlFromNodes } from '@lexical/html';

// sections for this page
import ForumsLexicalToolbar from "../ForumsLexical/ForumsLexicalToolbar.js";

import styles from "../../styles/jss/nextjs-material-kit/pages/forumsStyle.js";
import ImagesPlugin from "./ImagesPlugin.js";
import { InsertImageDialog } from "./ImagesPlugin.js";
import { ImageNode } from "./nodes/ImageNode.js";
import { EmojiNode } from "./nodes/EmojiNode.js";
import { MentionNode } from "./nodes/MentionNode.js";

const useStyles = makeStyles()(styles);

import {$generateNodesFromDOM} from '@lexical/html';
import EmojiPickerPlugin from "./EmojiPickerPlugin.js";
import MentionsPlugin from "./MentionsPlugin.js";

export default function ForumsLexicalEditor(props) {
    const { classes, cx } = useStyles();
    const { newPost, setNewPost } = props;

    const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);

    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [selection, setSelection] = useState(null);

    const editorRef = useRef(null);

    const lexicalTheme = {
        editor: classes.textEditor,
        placeholder: classes.editorPlaceholder,
        paragraph: classes.editorParagraph,
        text:
        {
          bold: classes.editorBold,
          italic: classes.editorItalic,
          underline: classes.editorUnderline
        }
    }

    const onError = (error) => {
        console.error(error);
    }

   // Component for initializing the editor with default content
  const InitializeContent = () => {
    const [editor] = useLexicalComposerContext();
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
      if (!isInitialized) {
        editor.update(() => {
          const parser = new DOMParser();
          const dom = parser.parseFromString(newPost, 'text/html');
          const root = $getRoot();

          if (root.getFirstChild() === null) {
            console.log("Attempting to append nodes...");

            // Generate nodes from parsed HTML
            const nodes = $generateNodesFromDOM(editor, dom);
            console.log(...nodes);
            if (nodes.length > 0) {
              root.append(...nodes);
              console.log("Nodes appended to editor");
            } else {
              console.log("No nodes generated from HTML. Adding fallback content.");

              // Fallback: Manually append content if no nodes are generated
              const paragraphNode = $createParagraphNode();
              const textNode = $createTextNode("This is fallback content.");

              paragraphNode.append(textNode);
              root.append(paragraphNode);
            }
          }
        });

        setIsInitialized(true); // Set flag to prevent infinite loop
      }
    }, [editor, isInitialized]);

    return null; // This component is only used for initialization
  };

    const initialConfig = {
        namespace: 'MyEditor',
        theme: lexicalTheme,
        onError,
        nodes: [
            LinkNode,
            // ImageNode,
            EmojiNode,
            MentionNode
          ],
        editorState: null,
    };

    const handleTextChange = () => {
        const editor = editorRef.current;
        editor.update(() => {
          setSelection($getSelection());
          const htmlString = $generateHtmlFromNodes(editor, null);
          setNewPost(htmlString);
      });
    };


    const returnImageDialog = () => {
      return (
      <InsertImageDialog
        activeEditor={editorRef.current}
        onClose={() => setImageDialogOpen(false)}
        captionsEnabled={false}
      />)
    }

    return (
        <div className={classes.textEditor} id="lexical-editor">
        <LexicalComposer initialConfig={initialConfig}>
            <ForumsLexicalToolbar setImageDialogOpen={setImageDialogOpen} imageDialogOpen={imageDialogOpen} setEmojiPickerOpen={setEmojiPickerOpen} emojiPickerOpen={emojiPickerOpen}/>
            <div className={classes.textEditorContainer}>
            <RichTextPlugin
                id="content"
                name="content"
                contentEditable={<ContentEditable/>}
                ErrorBoundary={LexicalErrorBoundary}
            />

            <OnChangePlugin onChange={handleTextChange} ignoreSelectionChange={true} />
            <HistoryPlugin />
            <AutoFocusPlugin />
            <EditorRefPlugin editorRef={editorRef} />
            <LinkPlugin />
            {/* <ImagesPlugin /> */}
            <EmojiPickerPlugin emojiPickerOpen={emojiPickerOpen} setEmojiPickerOpen={setEmojiPickerOpen}/>
            <MentionsPlugin anchorElementRef={selection}/>
            <InitializeContent />
            </div>
        </LexicalComposer>
        {imageDialogOpen && (
                returnImageDialog()
            )}
        </div>
    )
}
